import { useEffect, useRef, useState } from 'react';
import { dehydrate, Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import { GetServerSideProps, NextPage } from 'next';

import {
  BannerGroup,
  fetchShopIdByMainDomain,
  fetchStoreInfo,
  getComponentListInfiniteQueryOptions,
  Layout,
  ServerError,
  ShopInfo,
  StoreHomeHeader,
  TextCategoryList,
  useEnvConfig,
  useNetworkStatus,
  useStoreHomeUBL,
} from '@domains/storehome-common';
import { DefaultMainContainer, SelectedMainContainer } from '@domains/storehome-feature';

export interface StoreHomePageProps {
  shop_id: string;
  uuid: string;
  main_domain: string;
  shop_information: ShopInfo;
  banner_group: BannerGroup | null;
  category_list: TextCategoryList[];
  is_preview?: boolean;
  is_selected?: boolean;
  error?: unknown;
  dehydrated_state: unknown;
}

export const StoreHomePage: NextPage<StoreHomePageProps> = ({
  is_preview,
  error,
  shop_id,
  uuid,
  is_selected,
  main_domain,
  banner_group,
  category_list,
  shop_information,
  dehydrated_state,
}) => {
  const queryClientRef = useRef<QueryClient>(
    new QueryClient({ defaultOptions: { queries: { staleTime: 10000, refetchOnWindowFocus: false } } }),
  );
  const [is_header_solid, setIsHeaderSolid] = useState(false);

  const { isOffline } = useNetworkStatus();
  const { is_zigzag } = useEnvConfig();
  const { pageviewUBL } = useStoreHomeUBL(shop_id);

  useEffect(() => {
    if (!is_preview) {
      pageviewUBL('shop_detail', window.location.href, {
        is_opened: !error,
      });
    }
  }, []);

  useEffect(() => {
    if (!is_selected) {
      return;
    }

    if (!document.getElementById('storehome-banner-title')) {
      setIsHeaderSolid(true);
      return;
    }

    window.addEventListener('scroll', selectedScrollHeaderEvent, { passive: true });
    selectedScrollHeaderEvent();

    return () => {
      window.removeEventListener('scroll', selectedScrollHeaderEvent);
    };
  }, [is_selected]);

  const selectedScrollHeaderEvent = () => {
    const navigation_bar = document.getElementById('storehome-navigation-bar');
    const shop_info = document.getElementById('storehome-banner-title');

    if (!navigation_bar || !shop_info) {
      return;
    }

    const navigation_end = navigation_bar.offsetHeight + navigation_bar.offsetTop + window.scrollY;
    const shop_info_start = shop_info.offsetTop;
    if (navigation_end >= shop_info_start) {
      setIsHeaderSolid(true);
    } else {
      setIsHeaderSolid(false);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const should_render_header = !is_zigzag;

  if (error) {
    return (
      <>
        {should_render_header && !is_preview && (
          <StoreHomeHeader
            main_domain={main_domain}
            is_transparent_header={is_selected}
            is_solid_state={is_header_solid}
            baddge_background_color={is_selected ? vars.color.brandSelected : undefined}
          />
        )}
        <ServerError retry={handleRetry} height='calc(100vh - 100px)' />
      </>
    );
  }

  return (
    <QueryClientProvider client={queryClientRef.current}>
      <Hydrate state={dehydrated_state}>
        <Layout is_selected={is_selected}>
          {should_render_header && !is_preview && (
            <StoreHomeHeader
              main_domain={main_domain}
              show_search_button={isOffline ? false : shop_information.show_search_button}
              is_transparent_header={is_selected}
              is_solid_state={is_header_solid}
              baddge_background_color={is_selected ? vars.color.brandSelected : undefined}
            />
          )}
          {isOffline ? (
            <ServerError
              isNetworkError
              title='네트워크가 불안정해요'
              description={`Wi-Fi 또는 셀룰러 데이터 연결을 확인한 후\n다시 시도해주세요.`}
              retry={handleRetry}
              height='calc(100vh - 100px)'
            />
          ) : (
            <StoreHomeMain
              shop_info={shop_information}
              banner_group={banner_group}
              category_list={category_list}
              is_selected={is_selected}
              is_preview={is_preview}
              is_header_solid={is_header_solid}
            />
          )}
        </Layout>
      </Hydrate>
    </QueryClientProvider>
  );
};

const EXCLUDED_MAIN_DOMAINS = [
  'cq-e7nrhy0n', // 릿지서울 - 퇴점
  'cq-dm8mx5h0', // 비브앤러브 - 자사몰만 노출 희망
];

export const getStoreHomePageServerSideProps: GetServerSideProps<Partial<StoreHomePageProps>> = async ({
  query,
  req,
}) => {
  const main_domain = String(query.main_domain);

  const user_agent = req.headers['user-agent'] || '';
  const uuid = (req?.cookies && req.cookies.ZIGZAGUUID) ?? '';
  const is_crawler = /(Googlebot|Yeti|Naverbot)/i.test(user_agent);
  // TODO: 임시. PDP 상품 처럼 백엔드 api 요청 후 개선 필요. ([catalog_product_id]/index.tsx 참고)
  const should_render_not_found = is_crawler && EXCLUDED_MAIN_DOMAINS.includes(main_domain);

  // 검색 로봇 && 구매 불가 상품일 경우 404 리턴
  if (should_render_not_found) {
    return {
      notFound: true,
    };
  }

  const is_preview = typeof query.preview === 'string' ? Boolean(query.preview) : false;

  try {
    const shop_id = await fetchShopIdByMainDomain(main_domain, req);
    const client = new QueryClient();

    const store_info = await fetchStoreInfo(shop_id, req);

    if (store_info == null) {
      throw new Error('존재하지 않는 스토어입니다.');
    }
    const {
      shop_ux_shop: { banner_group, ...shop_information },
      shop_ux_category_component_list: { text_category_list: category_list },
    } = store_info;

    return {
      props: {
        uuid,
        shop_id,
        main_domain,
        shop_information,
        banner_group: is_preview ? null : banner_group,
        category_list,
        is_preview,
        is_selected: shop_information.store_home_theme === 'SELECTED',
        dehydrated_state: JSON.parse(JSON.stringify(dehydrate(client))),
      },
    };
  } catch (error) {
    return {
      props: {
        error: error instanceof Error ? error.message : String(error),
        shop_id: '',
        main_domain: '',
        uuid: '',
      },
    };
  }
};

export const getStoreHomeByIdPageServerSideProps: GetServerSideProps<Partial<StoreHomePageProps>> = async ({
  query,
  req,
}) => {
  const shop_id = String(query.shop_id);
  const uuid = (req?.cookies && req.cookies.ZIGZAGUUID) ?? '';
  const is_preview = typeof query.preview === 'string' ? Boolean(query.preview) : false;

  try {
    const client = new QueryClient();
    const [store_info] = await Promise.all([
      fetchStoreInfo(shop_id, req),
      client.fetchInfiniteQuery(
        getComponentListInfiniteQueryOptions(
          {
            shop_id,
            check_button_item_ids: [],
            sorting_item_id: null,
          },
          req,
        ),
      ),
    ]);
    if (store_info == null) {
      throw new Error('존재하지 않는 스토어입니다.');
    }
    const {
      shop_ux_shop: { banner_group, ...shop_information },
      shop_ux_category_component_list: { text_category_list: category_list },
    } = store_info;

    return {
      props: {
        shop_id,
        uuid,
        main_domain: shop_information.id,
        shop_information,
        banner_group: is_preview ? null : banner_group,
        category_list,
        is_preview,
        is_selected: shop_information.store_home_theme === 'SELECTED',
        dehydrated_state: JSON.parse(JSON.stringify(dehydrate(client))),
      },
    };
  } catch (error) {
    return {
      props: {
        error: true,
        shop_id: '',
        main_domain: '',
        uuid: '',
        is_selected: false,
      },
    };
  }
};

export interface StoreHomeMainProps {
  shop_info: ShopInfo;
  is_header_solid?: boolean;
  banner_group: BannerGroup | null;
  category_list: TextCategoryList[];
  is_selected?: boolean;
  is_preview?: boolean;
}

const StoreHomeMain = ({
  shop_info,
  is_header_solid,
  banner_group,
  category_list,
  is_selected,
  is_preview,
}: StoreHomeMainProps) => {
  if (is_selected) {
    return (
      <SelectedMainContainer
        shop_info={shop_info}
        banner_group={banner_group}
        category_list={category_list}
        is_preview={is_preview}
        is_header_solid={is_header_solid}
      />
    );
  }

  return (
    <DefaultMainContainer
      shop_info={shop_info}
      banner_group={banner_group}
      category_list={category_list}
      is_preview={is_preview}
      is_header_solid={is_header_solid}
    />
  );
};
